import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  startTransition,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  CssBaseline,
  styled,
} from '@mui/material';
import {
  Home as HomeIcon,
  Hotel as HotelIcon,
  Settings as SettingsIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import LandingHotels from '../components/landing/LandingHotels';
import LandingOrgDashboard from '../components/landing/LandingOrgDashboard';
import LandingSettings from '../components/landing/LandingSettings';
import LandingProfile from '../components/landing/LandingProfile';
import LandingUserGuide from '../components/landing/LandingUserGuide';
import ProfileDesktopMenu from '../components/profile/ProfileDesktopMenu';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import ThemeToggleButton from '../components/common/ThemeToggleButton';
import { getStaticFileUrl } from '../utils/staticUtils';
import AlertDialog from '../components/common/AlertDialog';
import useMobileView from '../hooks/useMobileView';
import MobileShellGraphBackdrop from '../components/layout/mobile/MobileShellGraphBackdrop';
import LandingMobileHeader from '../components/layout/mobile/LandingMobileHeader';
import MobileBottomNav from '../components/layout/mobile/MobileBottomNav';
import {
  mobileAppBackground,
  MOBILE_APP_HEADER_HEIGHT,
  mobileHideScrollbarSx,
} from '../components/layout/mobile/mobileAppTheme';
import { MOBILE_LAYOUT } from '../utils/mobileView';
import { getLandingBottomTabs } from '../config/landingMobileNavigation';
import { AUTH_LOGO_STATIC_PATH } from '../components/auth/loginDarkTheme';
import { THEME_COLORS as APP_THEME } from '../shared/constants/theme';
import { PERMISSIONS, getPermissionDeniedMessage } from '../auth/permissions';
import '../components/layout/MainLayout.css';
import './LandingPage.css';

// Constants - matching exact CSS values
const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 60;
const TOPBAR_HEIGHT = 60;

const DARK_CHROME_BG = APP_THEME.SURFACE_HEADER_DARK;
const DARK_CHROME_GRADIENT = `linear-gradient(180deg, ${APP_THEME.SURFACE_HEADER_DARK} 0%, ${APP_THEME.BACKGROUND_DARK} 100%)`;
const DARK_CHROME_BORDER = 'rgba(255, 255, 255, 0.08)';

/** `view` query → sidebar panel id (default: hotels). */
function viewFromSearch(search) {
  const urlParams = new URLSearchParams(search);
  const view = urlParams.get('view');
  if (view === 'dashboard') return 'dashboard';
  if (view === 'hotels') return 'hotels';
  if (view === 'settings') return 'settings';
  if (view === 'users') return 'settings'; // backward compatibility for old links
  if (view === 'profile') return 'profile';
  if (view === 'user-guide') return 'user-guide';
  return 'hotels';
}

function initialPanelsShown(search) {
  const m = viewFromSearch(search);
  return {
    hotels: m === 'hotels',
    dashboard: m === 'dashboard',
    settings: m === 'settings',
    profile: m === 'profile',
    'user-guide': m === 'user-guide',
  };
}

// Theme colors matching MainLayout dashboard chrome
const THEME_COLORS = {
  dark: {
    background: DARK_CHROME_BG,
    backgroundBody: APP_THEME.BACKGROUND_DARK,
    text: '#e2e8f0',
    textAdditional: 'rgba(148, 163, 184, 0.92)',
    hover: 'rgba(255, 255, 255, 0.06)',
    border: DARK_CHROME_BORDER,
    icon: 'rgba(148, 163, 184, 0.8)',
    accent: '#4ce1b6',
  },
  light: {
    background: '#ffffff',
    backgroundBody: '#f2f4f7',
    text: 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
    textAdditional: '#646777',
    hover: 'rgb(228.75, 234, 249.75)',
    border: '#eff1f5',
    icon: '#dddddd',
    accent: '#4ce1b6',
  },
};

// Styled AppBar — same surface as MainLayout StyledAppBar / left drawer
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isDark',
})(({ isDark }) => ({
  height: TOPBAR_HEIGHT,
  backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
  backgroundImage: isDark ? DARK_CHROME_GRADIENT : 'none',
  boxShadow: isDark ? '0 1px 0 rgba(255,255,255,0.04)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)',
  borderBottom: `1px solid ${isDark ? DARK_CHROME_BORDER : '#e8eaed'}`,
  zIndex: 101,
  '&.MuiAppBar-root': {
    backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
    backgroundImage: 'none',
    boxShadow: isDark ? '0 1px 30px 1px rgb(181 233 211 / 11%)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)',
  },
}));

// Styled Drawer matching MainLayout - Icon with Label App Sidebar
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isMobile',
})(({ isDark, isMobile }) => ({
  width: isMobile ? '100%' : '100px',
  flexShrink: 0,
  zIndex: isMobile ? 99 : 100,
  '& .MuiDrawer-paper': {
    width: isMobile ? '100%' : '100px',
    boxSizing: 'border-box',
    background: isDark ? DARK_CHROME_GRADIENT : '#ffffff',
    backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
    paddingTop: isMobile ? '0' : `${TOPBAR_HEIGHT}px`,
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isMobile
      ? '0 2px 8px rgba(0, 0, 0, 0.1)'
      : (isDark ? 'inset -1px 0 0 rgba(255,255,255,0.04)' : '0 1px 30px 1px rgba(0, 0, 0, 0.11)'),
    borderRight: isMobile ? 'none' : (isDark ? `1px solid ${DARK_CHROME_BORDER}` : '1px solid #e8eaed'),
    position: isMobile ? 'relative' : 'fixed',
    height: isMobile ? 'calc(100vh - 60px)' : '100vh',
    top: isMobile ? '60px' : 0,
    left: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    zIndex: isMobile ? 99 : 100,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      borderRadius: '3px',
      '&:hover': {
        background: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
}));

// Styled ListItemButton matching MainLayout - Fancy Creative Button Style
const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isSelected',
})(({ isDark, isSelected }) => ({
  height: 'auto',
  minHeight: '70px',
  width: 'calc(100% - 8px)',
  margin: '4px',
  padding: '12px 8px',
  position: 'relative',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  background: isSelected 
    ? (isDark 
        ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.18) 0%, rgba(76, 225, 182, 0.10) 100%)' 
        : 'linear-gradient(135deg, rgba(76, 225, 182, 0.15) 0%, rgba(76, 225, 182, 0.08) 100%)')
    : (isDark
        ? 'transparent'
        : 'rgba(0, 0, 0, 0.01)'),
  borderRadius: '12px',
  border: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderTop: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.5)' : 'rgba(76, 225, 182, 0.4)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderBottom: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'}`,
  borderLeft: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  borderRight: isSelected 
    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: isSelected 
    ? (isDark 
        ? '0 4px 12px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(76, 225, 182, 0.1)' 
        : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 0 1px rgba(76, 225, 182, 0.08)')
    : (isDark
        ? 'none'
        : '0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 1px rgba(255, 255, 255, 0.9)'),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: isSelected
    ? '#4ce1b6'
    : (isDark ? '#e2e8f0' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)'),
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: isSelected
      ? (isDark 
          ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.25) 0%, rgba(76, 225, 182, 0.15) 100%)' 
          : 'linear-gradient(135deg, rgba(76, 225, 182, 0.22) 0%, rgba(76, 225, 182, 0.12) 100%)')
      : (isDark 
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)' 
          : 'linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.04) 100%)'),
    transform: 'translateY(-2px)',
    boxShadow: isSelected
      ? (isDark 
          ? '0 6px 16px rgba(76, 225, 182, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(76, 225, 182, 0.15)' 
          : '0 6px 16px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 0 1px rgba(76, 225, 182, 0.12)')
      : (isDark 
          ? '0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.03)' 
          : '0 3px 8px rgba(0, 0, 0, 0.12), inset 0 1px 1px rgba(255, 255, 255, 0.95)'),
    border: isSelected 
      ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.5)' : 'rgba(76, 225, 182, 0.4)'}`
      : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
    borderTop: isSelected 
      ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.6)' : 'rgba(76, 225, 182, 0.5)'}`
      : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: isSelected ? '50px' : '0px',
    width: '4px',
    background: isSelected 
      ? 'linear-gradient(180deg, rgba(76, 225, 182, 1) 0%, rgba(76, 225, 182, 0.8) 100%)'
      : 'transparent',
    borderRadius: '0 3px 3px 0',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isSelected ? '0 0 12px rgba(76, 225, 182, 0.6), inset -1px 0 2px rgba(255, 255, 255, 0.2)' : 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: isSelected 
      ? 'radial-gradient(circle at 30% center, rgba(76, 225, 182, 0.15) 0%, transparent 60%)'
      : 'transparent',
    opacity: isSelected ? 1 : 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    borderRadius: '11px',
  },
  '&:active': {
    transform: 'translateY(0px) scale(0.97)',
    boxShadow: isSelected
      ? (isDark 
          ? '0 2px 6px rgba(76, 225, 182, 0.25), inset 0 2px 4px rgba(0, 0, 0, 0.15)' 
          : '0 2px 6px rgba(76, 225, 182, 0.2), inset 0 2px 4px rgba(0, 0, 0, 0.08)')
      : (isDark 
          ? 'inset 0 3px 6px rgba(0, 0, 0, 0.25)' 
          : 'inset 0 3px 6px rgba(0, 0, 0, 0.12)'),
  },
}));

// Styled ListItemIcon matching MainLayout
const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ isSelected }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  minWidth: '32px',
  width: '32px',
  height: '32px',
  margin: 0,
  marginBottom: '6px',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: isSelected ? '100%' : '0%',
    height: isSelected ? '100%' : '0%',
    borderRadius: '50%',
    background: isSelected ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '26px',
    width: '26px',
    height: '26px',
    color: isSelected
      ? '#4ce1b6'
      : 'rgba(148, 163, 184, 0.85)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 1,
    filter: isSelected ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
  },
}));

// Styled ListItemText for labels below icons matching MainLayout
const StyledListItemText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'isSelected',
})(({ isDark, isSelected }) => ({
  fontSize: '10px',
  lineHeight: '14px',
  fontWeight: isSelected ? 700 : 500,
  margin: 0,
  padding: 0,
  color: isSelected ? '#4ce1b6' : (isDark ? 'rgba(148, 163, 184, 0.92)' : '#666'),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'center',
  letterSpacing: '0.02em',
  maxWidth: '92px',
  textShadow: isSelected ? '0 1px 2px rgba(76, 225, 182, 0.2)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

/**
 * Landing Page Component
 * Matching the exact design, colors, fonts, and layout from the dashboard
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isCompact: useLandingMobileShell, isTabletNative } = useMobileView();
  const [currentTheme, setCurrentTheme] = useState(detectThemeSync());
  const isDark = currentTheme === 'dark';
  
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(
    () => typeof window !== 'undefined' && ['hotels', 'settings'].includes(viewFromSearch(window.location.search)),
  );
  const [error, setError] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const hotelsFetchDoneRef = useRef(false);
  const hotelsFetchInFlightRef = useRef(false);
  // Sidebar is always open on desktop, no toggle needed

  // Alert dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Alert');
  const [alertSeverity, setAlertSeverity] = useState('error');
  
  // Show alert dialog
  const showAlert = (message, title = 'Error', severity = 'error') => {
    setAlertMessage(message);
    setAlertTitle(title);
    setAlertSeverity(severity);
    setAlertDialogOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertDialogOpen(false);
  };
  
  const [selectedMenu, setSelectedMenu] = useState(() => viewFromSearch(location.search));
  const selectedSettingsTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'subscription' || tab === 'users') {
      return tab;
    }
    return null;
  }, [location.search]);

  const [panelsShown, setPanelsShown] = useState(() =>
    typeof window !== 'undefined' ? initialPanelsShown(window.location.search) : initialPanelsShown(''),
  );

  // Get CSRF token from cookie
  const getCsrfToken = useCallback(() => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  }, []);

  // Watch theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);
  
  // Keep panel mounted after first visit so switching tabs does not remount heavy views
  useEffect(() => {
    setPanelsShown((prev) => ({ ...prev, [selectedMenu]: true }));
  }, [selectedMenu]);

  // Sync sidebar selection from URL (browser back/forward) without re-running on every selectedMenu change
  useEffect(() => {
    const newMenu = viewFromSearch(location.search);
    setSelectedMenu((prev) => (newMenu === prev ? prev : newMenu));
  }, [location.search]);

  // Clear hotel from session if clear_hotel parameter is present
  useEffect(() => {
    const clearHotel = new URLSearchParams(location.search).get('clear_hotel');
    if (clearHotel) {
      axios.post('/onboarding/clear-hotel/', {}, { withCredentials: true }).catch(() => {
        // Ignore errors
      });
    }
  }, [location]);

  // Check authentication on mount
  useEffect(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    const user = djangoContext.user || {};
    
    // If no user ID, redirect to login immediately
    if (!user.id) {
      window.location.href = '/login/';
      return;
    }
  }, []);

  // Fetch hotels only when the Hotelier tab is active — avoids blocking other tabs on first paint
  const fetchHotels = useCallback(async () => {
    if (hotelsFetchDoneRef.current || hotelsFetchInFlightRef.current) {
      return;
    }
    hotelsFetchInFlightRef.current = true;

    const djangoContext = window.DJANGO_CONTEXT || {};
    const user = djangoContext.user || {};

    if (!user.id) {
      window.location.href = '/login/';
      hotelsFetchInFlightRef.current = false;
      return;
    }

    try {
      const response = await axios.get('/onboarding/api/landing-hotels/', {
        withCredentials: true,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.data) {
        const hotelsData = response.data.hotels || response.data || [];
        if (Array.isArray(hotelsData)) {
          if (hotelsData.length > 0) {
            setHotels(hotelsData);
            setError(null);
          } else {
            setHotels([]);
            setError(
              'No hotels available. Please ensure you have assigned hotels and an organisation. Contact your administrator to set up your account.',
            );
          }
        }
      } else {
        setHotels([]);
        setError(
          'No hotels available. Please ensure you have assigned hotels and an organisation. Contact your administrator to set up your account.',
        );
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view hotels.');
      } else if (err.response?.status === 401 || err.response?.status === 500) {
        setError('Please log in to view hotels.');
        setTimeout(() => {
          window.location.href = '/login/';
        }, 100);
      } else {
        setError('Failed to load hotels. Please try again later.');
      }
    } finally {
      hotelsFetchInFlightRef.current = false;
      hotelsFetchDoneRef.current = true;
      setHotelsLoading(false);
    }
  }, []);

  const refreshHotels = useCallback(async () => {
    hotelsFetchDoneRef.current = false;
    setHotelsLoading(true);
    await fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    if (selectedMenu !== 'hotels' && selectedMenu !== 'settings') {
      return;
    }
    if (hotelsFetchDoneRef.current) {
      return;
    }
    setHotelsLoading(true);
    fetchHotels();
  }, [selectedMenu, fetchHotels]);


  // Get user data from Django context
  const user = useMemo(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    return djangoContext.user || {};
  }, []);

  // Check if user is Owner
  const isOwner = useMemo(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    // Check is_owner flag first
    if (djangoContext.is_owner === true) {
      return true;
    }
    // Fallback: check user_groups array
    if (djangoContext.user_groups && Array.isArray(djangoContext.user_groups)) {
      return djangoContext.user_groups.some(
        (group) => group && group.toString().trim() === 'Owner',
      );
    }
    return false;
  }, []);

  const userPermissions = useMemo(() => {
    const djangoContext = window.DJANGO_CONTEXT || {};
    return Array.isArray(djangoContext.user_permissions) ? djangoContext.user_permissions : [];
  }, []);

  // Permission-based (role template or Additional Access), not hardcoded roles.
  const canViewOrgDashboard = useMemo(() => {
    return (
      userPermissions.includes('*')
      || userPermissions.includes(PERMISSIONS.DASHBOARD_VIEW)
    );
  }, [userPermissions]);

  const canManageUsers = useMemo(() => {
    return isOwner || userPermissions.includes('*') || userPermissions.includes('common.manage_users');
  }, [isOwner, userPermissions]);

  const canManageSubscription = useMemo(() => {
    return isOwner || userPermissions.includes('*') || userPermissions.includes('common.manage_subscription');
  }, [isOwner, userPermissions]);

  const orgDashboardDeniedMessage = useMemo(
    () => getPermissionDeniedMessage(PERMISSIONS.DASHBOARD_VIEW, 'Organisation Dashboard'),
    [],
  );

  // Event handlers
  const handleHotelClick = useCallback((hotelId) => {
    window.location.href = `/dashboard/?hotel_id=${hotelId}`;
  }, []);


  const handleProfileMenuOpen = useCallback((event) => {
    setProfileMenuAnchor(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setProfileMenuAnchor(null);
  }, []);

  // Profile menu handler
  const handleProfileClick = useCallback(() => {
    handleProfileMenuClose();
    startTransition(() => {
      setSelectedMenu('profile');
      navigate('/onboarding/landing/?view=profile', { replace: false });
    });
  }, [handleProfileMenuClose, navigate]);

  const handleLogout = useCallback(() => {
    handleProfileMenuClose();
    // Logout is CSRF exempt, so we can submit without token
    // But include it if available for better security
    const csrfToken = getCsrfToken();
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/logout/';
    
    // Include CSRF token if available, but don't block logout if missing
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfmiddlewaretoken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    
    document.body.appendChild(form);
    form.submit();
  }, [getCsrfToken, handleProfileMenuClose]);

  // Menu item handlers — startTransition keeps the shell responsive while the next panel loads
  const handleMenuClick = useCallback((key) => {
    if (key === 'dashboard' && !canViewOrgDashboard) {
      return;
    }
    startTransition(() => {
      if (key === 'dashboard') {
        setSelectedMenu('dashboard');
        navigate('/onboarding/landing/?view=dashboard', { replace: false });
      } else if (key === 'hotels') {
        setSelectedMenu('hotels');
        navigate('/onboarding/landing/?view=hotels', { replace: false });
      } else if (key === 'settings') {
        setSelectedMenu('settings');
        navigate('/onboarding/landing/?view=settings', { replace: false });
      } else if (key === 'user-guide') {
        setSelectedMenu('user-guide');
        navigate('/onboarding/landing/?view=user-guide', { replace: false });
      }
    });
  }, [navigate, canViewOrgDashboard]);

  // Deep-link ?view=dashboard without Owner/GM → Hotelier
  useEffect(() => {
    if (selectedMenu === 'dashboard' && !canViewOrgDashboard) {
      startTransition(() => {
        setSelectedMenu('hotels');
        navigate('/onboarding/landing/?view=hotels', { replace: true });
      });
    }
  }, [selectedMenu, canViewOrgDashboard, navigate]);

  // Menu items matching MainLayout structure
  const menuItems = useMemo(() => {
    const items = [
      {
        title: 'Dashboard',
        shortTitle: 'Dashboard',
        icon: 'lnr-home',
        key: 'dashboard',
        disabled: !canViewOrgDashboard,
        disabledTooltip: orgDashboardDeniedMessage,
        onClick: () => handleMenuClick('dashboard'),
      },
      {
        title: 'Hotelier',
        shortTitle: 'Hotelier',
        icon: 'lnr-hotel',
        key: 'hotels',
        onClick: () => handleMenuClick('hotels'),
      },
    ];
    
    items.push({
      title: 'Settings',
      shortTitle: 'Settings',
      icon: 'lnr-settings',
      key: 'settings',
      onClick: () => handleMenuClick('settings'),
    });
    
    return items;
  }, [handleMenuClick, canViewOrgDashboard, orgDashboardDeniedMessage]);

  // Footer menu items (User Guide)
  const footerMenuItems = useMemo(() => {
    return [
      {
        title: 'User Guide',
        shortTitle: 'User Guide',
        icon: 'lnr-user-guide',
        key: 'user-guide',
        onClick: () => handleMenuClick('user-guide'),
      },
    ];
  }, [handleMenuClick]);

  const landingBottomTabs = useMemo(
    () => getLandingBottomTabs(selectedMenu, handleMenuClick, {
      canViewOrgDashboard,
      deniedTooltip: orgDashboardDeniedMessage,
    }),
    [selectedMenu, handleMenuClick, canViewOrgDashboard, orgDashboardDeniedMessage]
  );

  // Icon mapping for menu items
  const iconMap = {
    'lnr-home': HomeIcon,
    'lnr-hotel': HotelIcon,
    'lnr-settings': SettingsIcon,
    'lnr-user-guide': MenuBookIcon,
  };

  // Fixed width for icon + label sidebar (matching MainLayout)
  const drawerWidth = 100;

  // Render menu item helper - matching MainLayout style
  const renderMenuItem = (item) => {
    const IconComponent = iconMap[item.icon] || HomeIcon;
    const selected = selectedMenu === item.key;
    const shortTitle = item.shortTitle || item.title;
    const isUserGuide = item.key === 'user-guide';
    const disabled = Boolean(item.disabled);

    const button = (
      <StyledListItemButton
        isDark={isDark}
        isSelected={selected && !disabled}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return;
          item.onClick?.();
        }}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.42 : 1,
          filter: disabled ? 'grayscale(0.45)' : 'none',
          '&:hover': disabled
            ? { backgroundColor: 'transparent' }
            : undefined,
        }}
      >
        <StyledListItemIcon isSelected={selected && !disabled}>
          {isUserGuide ? (
            <Box
              component="img"
              src={getStaticFileUrl('topbar/logo_icon_bg.png')}
              alt="User Guide"
              sx={{
                width: '26px',
                height: '26px',
                objectFit: 'contain',
                borderRadius: '4px',
                filter: selected && !disabled
                  ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.35))'
                  : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          ) : (
            <IconComponent />
          )}
        </StyledListItemIcon>
        <StyledListItemText
          isDark={isDark}
          isSelected={selected && !disabled}
        >
          {shortTitle}
        </StyledListItemText>
      </StyledListItemButton>
    );

    return (
      <ListItem
        key={item.key}
        disablePadding
        sx={{
          marginBottom: 0,
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          direction: 'ltr',
        }}
      >
        {disabled ? (
          <Tooltip title={item.disabledTooltip || orgDashboardDeniedMessage} arrow placement="right">
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              {button}
            </Box>
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  // Render menu section - matching MainLayout
  const renderMenuSection = (items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <Box sx={{ mb: 0, direction: 'ltr' }}>
        <List sx={{ padding: 0, margin: 0 }}>
          {items.map((item) => (
            <React.Fragment key={item.key}>
              {renderMenuItem(item)}
            </React.Fragment>
        ))}
      </List>
    </Box>
  );
  };

  return (
    <Box
      className={
        useLandingMobileShell
          ? 'mobile-app-shell landing-page-shell theme-dark'
          : undefined
      }
      sx={{
        display: 'flex',
        minHeight: useLandingMobileShell ? '100dvh' : '100vh',
        height: useLandingMobileShell ? '100dvh' : 'auto',
        maxHeight: useLandingMobileShell ? '100dvh' : 'none',
        position: useLandingMobileShell ? 'relative' : undefined,
        overflow: useLandingMobileShell ? 'hidden' : undefined,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        ...(useLandingMobileShell ? { backgroundColor: 'transparent', background: 'none' } : {}),
      }}
    >
      <CssBaseline />

      {useLandingMobileShell ? <MobileShellGraphBackdrop /> : null}

      {useLandingMobileShell ? (
        <LandingMobileHeader
          user={user}
          onProfileClick={handleProfileMenuOpen}
          onLogoClick={() => handleMenuClick('dashboard')}
        />
      ) : null}
      
      {/* AppBar - Header (desktop / non-shell mobile web) */}
      {!useLandingMobileShell ? (
      <StyledAppBar
        position="fixed"
        elevation={0}
        isDark={isDark}
        sx={
          isTabletNative
            ? {
                pt: 'env(safe-area-inset-top)',
                height: `calc(${TOPBAR_HEIGHT}px + env(safe-area-inset-top))`,
              }
            : undefined
        }
      >
        <Toolbar 
          sx={{ 
            minHeight: '0 !important',
          height: TOPBAR_HEIGHT,
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            '&.MuiToolbar-root': {
              minHeight: '0 !important',
              paddingLeft: '0',
              paddingRight: '0',
            },
            '&.css-1brtlba': {
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
            },
          }}
        >
          {/* Left section - Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', flex: '0 0 auto', ml: { xs: 1, md: 2 }, minWidth: 0, overflow: 'hidden' }}>
          {/* Logo */}
          <Box
            component="img"
            src={getStaticFileUrl(isDark ? 'topbar/logo_ed_dark.png' : 'topbar/logo_ed.png')}
            alt="RoomNexa"
              onClick={() => {
                startTransition(() => {
                  setSelectedMenu('dashboard');
                  navigate('/onboarding/landing/?view=dashboard');
                });
              }}
            sx={{
                width: 'auto',
              height: '46px',
                maxWidth: { xs: '200px', md: 'none' },
              mr: 2,
              objectFit: 'contain',
              objectPosition: 'left center',
                cursor: 'pointer',
            }}
          />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right section - Theme and Profile */}
          <Box sx={{ 
            position: 'absolute',
            right: 0,
            display: 'flex',
            height: '100%',
            marginRight: { xs: '8px', md: '15px' },
            alignItems: 'center',
            flexShrink: 0,
            minWidth: 0,
          }}>
          <ThemeToggleButton compact={isTabletNative || useLandingMobileShell} sx={{ mx: 0.25 }} />

          {/* Profile menu */}
          <IconButton 
            onClick={handleProfileMenuOpen} 
            sx={{
                width: 'auto',
                height: '100%',
                padding: '0 15px',
                color: isDark ? '#e2e8f0' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
                backgroundColor: 'transparent',
              '&:hover': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgb(228.75, 234, 249.75)',
              },
            }}
            aria-label="account menu"
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                  bgcolor: '#4ce1b6',
                fontSize: '14px',
                fontWeight: 400,
                  margin: 'auto 0',
              }}
            >
              {user.username?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          </Box>
        </Toolbar>
      </StyledAppBar>
      ) : null}

      {/* Profile menu (shared by AppBar and landing mobile header) */}
      <ProfileDesktopMenu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        isDark={isDark}
        user={user}
        onMyProfile={handleProfileClick}
        onLockScreen={handleProfileMenuClose}
        onLogout={handleLogout}
      />

      {/* Mobile Horizontal Menu Bar - legacy web mobile only */}
      {isMobile && !useLandingMobileShell && (
      <Box
        component="nav"
        sx={{
            position: 'fixed',
            top: TOPBAR_HEIGHT,
            left: 0,
            right: 0,
            height: '80px',
            backgroundColor: isDark ? DARK_CHROME_BG : '#ffffff',
            borderBottom: `1px solid ${isDark ? DARK_CHROME_BORDER : '#e8eaed'}`,
            zIndex: 99,
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            alignItems: 'center',
            px: 0.5,
            py: 0.5,
            gap: 0.5,
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark 
                ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.4) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                : 'linear-gradient(90deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.15) 100%)',
              borderRadius: '10px',
              border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)'}`,
            },
          }}
        >
          {/* All menu items in horizontal layout */}
          {[...menuItems, ...footerMenuItems].map((item) => {
            const IconComponent = iconMap[item.icon] || HomeIcon;
            const selected = selectedMenu === item.key;
            const shortTitle = item.shortTitle || item.title;
            
            return (
              <Box
                key={item.key}
                onClick={item.onClick}
                sx={{
                  minWidth: '68px',
                  width: '68px',
                  height: '68px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  margin: 0,
                  padding: '8px 4px',
                  flexShrink: 0,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: selected
                    ? (isDark
                        ? 'linear-gradient(135deg, rgba(76, 225, 182, 0.15) 0%, rgba(76, 225, 182, 0.08) 100%)'
                        : 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.06) 100%)')
                    : 'transparent',
                  borderTop: selected
                    ? `2px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.28)'}`
                    : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                  borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                  boxShadow: selected
                    ? (isDark
                        ? '0 4px 12px rgba(76, 225, 182, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
                        : '0 4px 12px rgba(76, 225, 182, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8)')
                    : (isDark
                        ? 'none'
                        : '0 1px 3px rgba(0, 0, 0, 0.08)'),
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: selected ? '60%' : '0%',
                    height: selected ? '4px' : '0px',
                    background: 'linear-gradient(90deg, #4ce1b6 0%, rgba(76, 225, 182, 0.6) 100%)',
                    borderRadius: '0 0 4px 4px',
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: selected
                      ? (isDark
                          ? '0 6px 16px rgba(76, 225, 182, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.15)'
                          : '0 6px 16px rgba(76, 225, 182, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.9)')
                      : (isDark
                          ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                          : '0 4px 8px rgba(0, 0, 0, 0.12)'),
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '28px',
                    width: '28px',
                    height: '28px',
                    marginBottom: '4px',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      width: selected ? '100%' : '0%',
                      height: selected ? '100%' : '0%',
                      borderRadius: '50%',
                      background: selected ? 'rgba(76, 225, 182, 0.15)' : 'transparent',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 0,
                    },
                  }}
                >
                  {item.key === 'user-guide' ? (
                    <Box
                      component="img"
                      src={getStaticFileUrl('topbar/logo_icon_bg.png')}
                      alt="User Guide"
                      sx={{
                        width: '24px',
                        height: '24px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        position: 'relative',
                        zIndex: 1,
                        filter: selected
                          ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.35))'
                          : 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                  ) : (
                    <IconComponent
                      sx={{
                        fontSize: '24px',
                        width: '24px',
                        height: '24px',
                        color: selected ? '#4ce1b6' : 'rgba(148, 163, 184, 0.85)',
                        position: 'relative',
                        zIndex: 1,
                        filter: selected ? 'drop-shadow(0 2px 4px rgba(76, 225, 182, 0.3))' : 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                  )}
                </Box>
                {/* Label */}
                <Typography
                  sx={{
                    fontSize: '9px',
                    lineHeight: '12px',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#4ce1b6' : (isDark ? 'rgba(148, 163, 184, 0.92)' : '#666'),
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                    textShadow: selected ? '0 1px 2px rgba(76, 225, 182, 0.2)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {shortTitle}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Desktop Vertical Sidebar Navigation — hidden in phone mobile shell */}
      <Box
        component="nav"
        sx={{
          width: useLandingMobileShell ? 0 : { xs: 0, md: `${drawerWidth}px` },
          flexShrink: 0,
          transition: 'width 0.3s',
          display: useLandingMobileShell ? 'none' : { xs: 'none', md: 'block' },
        }}
      >
        <StyledDrawer
          variant="permanent"
          open={true}
          isDark={isDark}
          isMobile={isMobile}
          sx={{
            display: useLandingMobileShell ? 'none' : { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: `${drawerWidth}px`,
              transition: 'width 0.3s',
            },
          }}
        >
          {/* Paper paddingTop clears fixed AppBar — matches MainLayout; menu starts right under top bar */}
          {/* Scrollable Sidebar Content - Matching MainLayout Design */}
          <Box
            sx={{
              height: isTabletNative
                ? `calc(100vh - ${TOPBAR_HEIGHT}px - env(safe-area-inset-top))`
                : `calc(100vh - ${TOPBAR_HEIGHT}px)`,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              direction: 'rtl',
              '& > *': {
                direction: 'ltr',
              },
              '&::-webkit-scrollbar': {
                width: '6px',
                left: 0,
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
                margin: '8px 0',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark 
                  ? 'linear-gradient(180deg, rgba(76, 225, 182, 0.4) 0%, rgba(76, 225, 182, 0.2) 100%)' 
                  : 'linear-gradient(180deg, rgba(76, 225, 182, 0.3) 0%, rgba(76, 225, 182, 0.15) 100%)',
                borderRadius: '10px',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)'}`,
                '&:hover': {
                  background: isDark 
                    ? 'linear-gradient(180deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 100%)' 
                    : 'linear-gradient(180deg, rgba(76, 225, 182, 0.5) 0%, rgba(76, 225, 182, 0.3) 100%)',
                },
              },
            }}
          >
            {/* Main Menu Items */}
            <Box sx={{ flex: 1 }}>
              {renderMenuSection(menuItems)}
            </Box>
            
            {/* Footer Menu Items (User Guide) */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              {renderMenuSection(footerMenuItems)}
            </Box>
          </Box>
        </StyledDrawer>
      </Box>

              {/* Main Content */}
      <Box
        component="main"
        className={useLandingMobileShell ? 'landing-panel-surface' : undefined}
        sx={{
          flexGrow: 1,
          width: useLandingMobileShell ? '100%' : { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: useLandingMobileShell ? '100vw' : { xs: '100vw', md: `calc(100vw - ${drawerWidth}px)` },
          paddingTop: useLandingMobileShell
            ? `calc(${MOBILE_APP_HEADER_HEIGHT}px + env(safe-area-inset-top) + 8px)`
            : isTabletNative
              ? `calc(${TOPBAR_HEIGHT}px + env(safe-area-inset-top))`
              : { xs: `${TOPBAR_HEIGHT + 80}px`, md: `${TOPBAR_HEIGHT}px` },
          paddingBottom: useLandingMobileShell ? MOBILE_LAYOUT.mobileMainBottomInset : 0,
          minHeight: useLandingMobileShell ? 0 : '100vh',
          position: useLandingMobileShell ? 'relative' : undefined,
          zIndex: useLandingMobileShell ? 1 : undefined,
          backgroundColor: useLandingMobileShell
            ? 'transparent'
            : (isDark ? '#12151c' : '#f2f4f7'),
          transition: 'width 0.3s, background-color 0.3s ease, padding-top 0.3s',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: useLandingMobileShell ? 'auto' : 'visible',
          boxSizing: 'border-box',
          ...(useLandingMobileShell ? mobileHideScrollbarSx : {}),
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', position: 'relative' }}>
            {panelsShown.hotels && (
              <Box
                sx={{ display: selectedMenu === 'hotels' ? 'block' : 'none' }}
                aria-hidden={selectedMenu !== 'hotels'}
              >
                <LandingHotels
                  hotels={hotels || []}
                  loading={hotelsLoading}
                  error={error}
                  onHotelClick={handleHotelClick}
                  canManageSubscription={canManageSubscription}
                />
              </Box>
            )}

            {panelsShown.dashboard && canViewOrgDashboard && (
              <Box
                sx={{ display: selectedMenu === 'dashboard' ? 'block' : 'none' }}
                aria-hidden={selectedMenu !== 'dashboard'}
              >
                <LandingOrgDashboard />
              </Box>
            )}

            {panelsShown.settings && (
              <Box
                sx={{ display: selectedMenu === 'settings' ? 'block' : 'none' }}
                aria-hidden={selectedMenu !== 'settings'}
              >
                <LandingSettings
                  hotels={hotels}
                  hotelsLoading={hotelsLoading}
                  onRefreshHotels={refreshHotels}
                  canManageUsers={canManageUsers}
                  canManageSubscription={canManageSubscription}
                  initialTab={selectedSettingsTab}
                />
              </Box>
            )}

            {panelsShown.profile && (
              <Box
                sx={{ display: selectedMenu === 'profile' ? 'block' : 'none' }}
                aria-hidden={selectedMenu !== 'profile'}
              >
                <LandingProfile />
              </Box>
            )}

            {panelsShown['user-guide'] && (
              <Box
                sx={{ display: selectedMenu === 'user-guide' ? 'block' : 'none' }}
                aria-hidden={selectedMenu !== 'user-guide'}
              >
                <LandingUserGuide />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {useLandingMobileShell ? (
        <MobileBottomNav
          leftTabs={landingBottomTabs.leftTabs}
          rightTabs={landingBottomTabs.rightTabs}
          showMoreTab={false}
          onFabClick={() => handleMenuClick('hotels')}
          centerFabAriaLabel="Hotelier"
          centerFabContent={
            <Box
              component="img"
              src={getStaticFileUrl(AUTH_LOGO_STATIC_PATH)}
              alt="RoomNexa"
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
          }
        />
      ) : null}

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialogOpen}
        onClose={handleCloseAlert}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />
    </Box>
  );
};

export default LandingPage;
